import React, {useEffect, useMemo, useState} from 'react'
import {Input} from '@/components/ui/input'
import {Search, ExternalLink, Download} from 'lucide-react'
import {sanity} from '../lib/sanity'

const RESEARCH_PAGE_QUERY = `*[_type == "researchPage"][0]{
  label,
  title,
  intro
}`

const RESEARCH_DOCUMENTS_QUERY = `*[_type == "researchDocument"] | order(location asc, title asc) {
  _id,
  location,
  title,
  linkType,
  externalUrl,
  "pdfUrl": pdfFile.asset->url,
  "pdfFilename": pdfFile.asset->originalFilename
}`

function getActionLabel(item) {
  if (item.linkType === 'download') return 'Direct download'
  if (item.linkType === 'inProgress') return 'Onderzoek loopt nog'
  return 'Link naar externe website'
}

function getHref(item) {
  if (item.linkType === 'download') return item.pdfUrl
  if (item.linkType === 'external') return item.externalUrl
  return null
}

function ResearchDownloadsPage() {
  const [page, setPage] = useState(null)
  const [researchDocuments, setResearchDocuments] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    Promise.all([
      sanity.fetch(RESEARCH_PAGE_QUERY),
      sanity.fetch(RESEARCH_DOCUMENTS_QUERY),
    ])
      .then(([pageData, documentData]) => {
        setPage(pageData)
        setResearchDocuments(documentData || [])
        setLoading(false)
      })
      .catch((error) => {
        console.error(error)
        setLoading(false)
      })
  }, [])

  const filteredLinks = useMemo(() => {
    const query = search.trim().toLowerCase()

    if (!query) return researchDocuments

return researchDocuments.filter((item) => {
      return (
        item.location?.toLowerCase().includes(query) ||
        item.title?.toLowerCase().includes(query) ||
        getActionLabel(item).toLowerCase().includes(query)
      )
    })
  }, [search, researchDocuments])

  if (loading) return null

  return (
    <main className="min-h-screen px-8 pb-24 pt-28 text-[#5757fd] md:px-8">
      <section className="mx-auto mb-16 max-w-3xl text-start">
        {page?.label && (
          <p className="mb-4 w-fit rounded-full bg-white/60 px-4 p-2 font-sans_bold text-sm uppercase tracking-wider">
            {page.label}
          </p>
        )}

        {page?.title && (
          <h1 className="text-4xl font-sans_bold leading-[0.95] md:text-7xl">
            {page.title}
          </h1>
        )}

        {page?.intro && (
          <p className="mx-auto mt-8 whitespace-pre-line font-sans text-lg">
            {page.intro}
          </p>
        )}

        <div className="mt-10 flex max-w-xl items-center gap-3 rounded-full bg-white px-5 py-4 shadow-[0_18px_40px_rgba(87,87,253,0.12)]">
          <Search className="h-5 w-5 shrink-0" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Zoek op locatie of documenttitel"
            className="border-0 bg-transparent p-0 text-[#5757fd] shadow-none placeholder:text-[#5757fd]/60 focus-visible:ring-0"
          />
        </div>
      </section>

      <section className="mx-auto max-w-4xl rounded-[2rem] bg-white px-6 py-4 font-sans md:px-10">
        {filteredLinks.length > 0 ? (
          filteredLinks.map((item, index) => {
            const href = getHref(item)
            const actionLabel = getActionLabel(item)
            const isDownload = item.linkType === 'download'

            return (
              <div key={item._id}>
                <div className="grid gap-4 py-8 md:grid-cols-[220px_1fr] md:items-start">
                  <h2 className="text-2xl font-bold text-[#5757fd]">
                    {item.location}
                  </h2>

                  {href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      download={isDownload ? item.pdfFilename || true : undefined}
                      className="group flex items-center justify-between gap-6 rounded-2xl bg-[#f3f2ff] p-5 transition-all duration-300 hover:bg-background"
                    >
                      <div className="text-[#5757fd] group-hover:text-[#5757fd]">
                        <p className="text-lg font-sans_bold leading-tight">
                          {item.title}
                        </p>
                        <p className="mt-2 font-sans text-base opacity-70">
                          {actionLabel}
                        </p>
                      </div>

                      {isDownload ? (
                        <Download className="mt-1 h-5 w-5 shrink-0 text-[#5757fd] transition-transform duration-300 group-hover:translate-y-0.5" />
                      ) : (
                        <ExternalLink className="mt-1 h-5 w-5 shrink-0 text-[#5757fd] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                      )}
                    </a>
                  ) : (
                    <div className="flex items-center justify-between gap-6 rounded-2xl bg-[#f3f2ff] p-5 opacity-70">
                      <div className="text-[#5757fd]">
                        <p className="text-lg font-sans_bold leading-tight">
                          {item.title}
                        </p>
                        <p className="mt-2 font-sans text-base opacity-70">
                          {actionLabel}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {index !== filteredLinks.length - 1 && (
                  <div className="h-px bg-[#5757fd]/20" />
                )}
              </div>
            )
          })
        ) : (
          <div className="py-16 text-center">
            <p className="text-xl font-bold">Geen documenten gevonden.</p>
          </div>
        )}
      </section>
    </main>
  )
}

export default ResearchDownloadsPage